import React, { useState } from 'react';
import { Block, Workflow } from '../types';

interface DeleteBlockModalProps {
  isOpen: boolean;
  onClose: () => void;
  block: Block | null;
  usedInWorkflows: Workflow[];
  onConfirmDelete: () => Promise<void>;
}

export const DeleteBlockModal: React.FC<DeleteBlockModalProps> = ({
  isOpen,
  onClose,
  block,
  usedInWorkflows,
  onConfirmDelete
}) => {
  const [confirmText, setConfirmText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const isUsed = usedInWorkflows.length > 0;
  const isDeleteDisabled = isUsed && confirmText !== 'DELETE';
  
  const handleDelete = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      await onConfirmDelete();
      onClose();
    } catch (err) {
      setError(`Error deleting block: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setIsLoading(false);
    }
  };
  
  if (!isOpen || !block) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                <svg className="h-6 w-6 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Delete Block
                </h3>
                <div className="mt-2">
                  <p className="text-sm text-gray-500">
                    Are you sure you want to delete the block "{block.name}"? This action cannot be undone.
                  </p>
                  
                  {isUsed && (
                    <div className="mt-4">
                      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
                        <div className="flex">
                          <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <div className="ml-3">
                            <p className="text-sm text-yellow-700">
                              <strong>Warning:</strong> This block is used in {usedInWorkflows.length} workflow{usedInWorkflows.length > 1 ? 's' : ''}.
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Used in workflows:</h4>
                      <ul className="bg-gray-50 p-2 rounded-md text-sm max-h-40 overflow-y-auto">
                        {usedInWorkflows.map(workflow => (
                          <li key={workflow.id} className="py-1 border-b border-gray-200 last:border-0">
                            {workflow.name}
                          </li>
                        ))}
                      </ul>
                      
                      <div className="mt-4">
                        <p className="text-sm text-red-600 font-semibold mb-2">
                          To delete this block, type DELETE in the field below:
                        </p>
                        <input
                          type="text"
                          className="w-full p-2 border border-gray-300 rounded-md text-sm"
                          value={confirmText}
                          onChange={(e) => setConfirmText(e.target.value)}
                          placeholder="Type DELETE here"
                        />
                      </div>
                    </div>
                  )}
                  
                  {error && (
                    <div className="mt-3 p-3 bg-red-50 text-red-700 rounded-md text-sm">
                      {error}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white ${isDeleteDisabled ? 'bg-gray-400 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500'} sm:ml-3 sm:w-auto sm:text-sm`}
              onClick={handleDelete}
              disabled={isDeleteDisabled || isLoading}
            >
              {isLoading ? 'Deleting...' : 'Delete'}
            </button>
            <button
              type="button"
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}; 